import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  updateDoc,
  deleteDoc,
  enableNetwork,
  addDoc,
  startAfter,
  limit as firestoreLimit
} from 'firebase/firestore';
import { deleteObject, ref as storageRef } from 'firebase/storage';
import { firestore, storage, isNetworkAvailable, resetFirebaseConnection, clearFirestoreCache } from '../firebase';

/**
 * Utility function to handle Firestore errors
 * @param {Error} error - The error object from Firestore
 * @param {string} operation - Description of the operation that failed
 * @returns {Promise<boolean>} - Whether the error was handled/recovered
 */
export const handleFirestoreError = async (error, operation = 'database operation') => {
  console.error(`Error during ${operation}:`, error);
  
  // Check for internal assertion errors which require cache clearing
  if (
    error.message?.includes('INTERNAL ASSERTION FAILED') ||
    error.message?.includes('Unexpected state')
  ) {
    console.log('Detected Firestore internal assertion failure. Attempting to clear cache...');
    const clearResult = await clearFirestoreCache();
    
    if (clearResult.success) {
      console.log('Successfully cleared Firestore cache');
      return true;
    } else {
      console.error('Failed to clear Firestore cache:', clearResult.error);
      
      // If cache clearing failed, try connection reset as fallback
      console.log('Attempting connection reset as fallback...');
      const resetResult = await resetFirebaseConnection();
      
      if (resetResult.success) {
        console.log('Successfully reset Firestore connection after cache clear failure');
        return true;
      }
    }
  }
  // Check for other connection issues that might need network reset
  else if (
    error.code === 'unavailable' || 
    error.code === 'failed-precondition'
  ) {
    console.log('Detected Firestore connection issue. Attempting to reset connection...');
    const resetResult = await resetFirebaseConnection();
    
    if (resetResult.success) {
      console.log('Successfully reset Firestore connection');
      return true;
    } else {
      console.error('Failed to reset Firestore connection:', resetResult.error);
    }
  }
  
  // Return false to indicate error wasn't fully handled
  return false;
};

/**
 * Get all workspaces for a user
 * @param {string} userId - The user ID to get workspaces for
 * @returns {Promise<Array>} - Promise resolving to array of workspace objects
 */
export const getWorkspaces = async (userId) => {
  if (!userId) {
    throw new Error('User ID is required');
  }

  const hasWorkspaceAccess = (data, currentUserId) => {
    const members = Array.isArray(data.members) ? data.members : [];
    return data.ownerId === currentUserId ||
      data.owner === currentUserId ||
      members.includes(currentUserId) ||
      members.some(member => member?.user === currentUserId || member?.userId === currentUserId);
  };

  const buildWorkspaceMapFromSnapshot = (snapshot, currentUserId) => {
    const workspaceMap = new Map();

    snapshot.docs.forEach(docSnap => {
      const data = docSnap.data();
      if (!hasWorkspaceAccess(data, currentUserId)) return;

      workspaceMap.set(docSnap.id, {
        id: docSnap.id,
        ...data,
        role: data.ownerId === currentUserId || data.owner === currentUserId ? 'owner' : 'member'
      });
    });

    return Array.from(workspaceMap.values());
  };

  try {
    // Get workspaces where user is owner
    const ownerIdQuery = query(
      collection(firestore, 'workspaces'),
      where('ownerId', '==', userId),
      orderBy('updatedAt', 'desc')
    );

    const ownerQuery = query(
      collection(firestore, 'workspaces'),
      where('owner', '==', userId),
      orderBy('updatedAt', 'desc')
    );

    // Get workspaces where user is member
    const memberQuery = query(
      collection(firestore, 'workspaces'),
      where('members', 'array-contains', userId),
      orderBy('updatedAt', 'desc')
    );

    const [ownerIdSnapshot, ownerSnapshot, memberSnapshot] = await Promise.all([
      getDocs(ownerIdQuery),
      getDocs(ownerQuery),
      getDocs(memberQuery)
    ]);

    // Combine and deduplicate workspaces
    const workspaces = new Map();

    ownerIdSnapshot.docs.forEach(doc => {
      workspaces.set(doc.id, {
        id: doc.id,
        ...doc.data(),
        role: 'owner'
      });
    });

    ownerSnapshot.docs.forEach(doc => {
      workspaces.set(doc.id, {
        id: doc.id,
        ...doc.data(),
        role: 'owner'
      });
    });

    memberSnapshot.docs.forEach(doc => {
      if (!workspaces.has(doc.id)) {
        workspaces.set(doc.id, {
          id: doc.id,
          ...doc.data(),
          role: 'member'
        });
      }
    });

    // Fallback for legacy/object-based members list entries.
    if (workspaces.size === 0) {
      const allAccessible = await getDocs(collection(firestore, 'workspaces'));
      allAccessible.docs.forEach(doc => {
        const data = doc.data();
        const hasAccess = hasWorkspaceAccess(data, userId);

        if (hasAccess) {
          workspaces.set(doc.id, {
            id: doc.id,
            ...data,
            role: data.ownerId === userId || data.owner === userId ? 'owner' : 'member'
          });
        }
      });
    }

    return Array.from(workspaces.values());
  } catch (error) {
    console.error('Error fetching workspaces:', error);

    // Fallback path: avoid hard-failing the app if indexed queries aren't available yet.
    try {
      const allAccessible = await getDocs(collection(firestore, 'workspaces'));
      return buildWorkspaceMapFromSnapshot(allAccessible, userId);
    } catch (fallbackError) {
      console.error('Error fetching workspaces via fallback query:', fallbackError);
      return [];
    }
  }
};

/**
 * Get a single workspace by ID
 * @param {string} workspaceId - The workspace ID to fetch
 * @returns {Promise<Object>} - Promise resolving to the workspace object
 */
export const getWorkspace = async (workspaceId) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }

  try {
    const docRef = doc(firestore, 'workspaces', workspaceId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Workspace not found');
    }

    return {
      id: docSnap.id,
      ...docSnap.data()
    };
  } catch (error) {
    console.error('Error fetching workspace:', error);
    throw new Error('Failed to fetch workspace: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Create a new workspace
 * @param {object} workspaceData - Data for the new workspace
 * @param {string} userId - User ID of the workspace owner
 * @returns {Promise<Object>} - Promise resolving to the created workspace
 */
export const createWorkspace = async (workspaceData, userId) => {
  console.log('createWorkspace called with:', { workspaceData, userId });
  
  if (!workspaceData || !userId) {
    console.error('Missing required parameters:', { workspaceData, userId });
    throw new Error('Workspace data and user ID are required');
  }

  // Check if we're online
  const networkAvailable = isNetworkAvailable();
  console.log('Network availability:', networkAvailable);
  
  if (!networkAvailable) {
    try {
      // Try to re-enable network before proceeding
      console.log('Attempting to re-enable network connection...');
      await enableNetwork(firestore);
      console.log('Network connection re-enabled');
    } catch (networkError) {
      console.error('Failed to re-enable network:', networkError);
      throw new Error('Cannot create workspace while offline. Please check your internet connection and try again.');
    }
  }

  try {
    console.log('Creating new workspace - Firestore instance:', !!firestore);
    
    // Generate a new ID
    const workspaceRef = doc(collection(firestore, 'workspaces'));
    console.log('Created workspace reference with ID:', workspaceRef.id);
    
    // Format workspace data
    const timestamp = new Date().toISOString();
    const workspace = {
      ...workspaceData,
      id: workspaceRef.id,
      ownerId: userId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      // Fallback for offline mode
      clientCreatedAt: timestamp,
      clientUpdatedAt: timestamp,
      members: [userId], // Add owner as first member
      documents: [],
      // Additional required fields
      settings: {
        theme: 'dark',
        notifications: true,
        sharing: true
      },
      stats: {
        documentCount: 0,
        memberCount: 1,
        lastActivity: serverTimestamp()
      },
      permissions: {
        public: false,
        allowComments: true,
        allowSharing: true
      }
    };
    
    console.log('Formatted workspace data:', workspace);
    
    // Save to Firestore
    console.log('Saving workspace to Firestore...');
    await setDoc(workspaceRef, workspace);
    console.log('Workspace saved successfully');

    // Track the workspace creation activity
    await addDoc(collection(firestore, 'activities'), {
      userId,
      type: 'workspace_created',
      targetId: workspaceRef.id,
      targetType: 'workspace',
      targetName: workspaceData.name,
      timestamp: serverTimestamp(),
      metadata: {
        workspaceId: workspaceRef.id,
        description: workspaceData.description
      }
    });
    
    // Return the new workspace with the generated ID
    return {
      ...workspace,
      id: workspaceRef.id
    };
  } catch (error) {
    console.error('Error creating workspace in Firestore:', error);
    
    if (error.code === 'unavailable' || error.code === 'failed-precondition' || 
        error.message?.includes('offline') || error.message?.includes('network')) {
      throw new Error('Failed to create workspace: You appear to be offline. Please check your internet connection and try again.');
    }
    
    throw new Error('Failed to create workspace: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Update a workspace
 * @param {string} workspaceId - ID of the workspace to update
 * @param {object} updates - Updates to apply to the workspace
 * @returns {Promise<Object>} - Promise resolving to the updated workspace
 */
export const updateWorkspace = async (workspaceId, updates) => {
  if (!workspaceId || !updates) {
    throw new Error('Workspace ID and updates are required');
  }

  try {
    const workspaceRef = doc(firestore, 'workspaces', workspaceId);
    
    // Add updated timestamp
    const updatedData = {
      ...updates,
      updatedAt: serverTimestamp()
    };
    
    await updateDoc(workspaceRef, updatedData);
    
    // Fetch the updated workspace
    const updatedDoc = await getDoc(workspaceRef);
    
    return {
      id: updatedDoc.id,
      ...updatedDoc.data()
    };
  } catch (error) {
    console.error('Error updating workspace:', error);
    throw new Error('Failed to update workspace');
  }
};

/**
 * Delete a workspace
 * @param {string} workspaceId - ID of the workspace to delete
 * @returns {Promise<void>}
 */
export const deleteWorkspace = async (workspaceId) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }

  try {
    const workspaceRef = doc(firestore, 'workspaces', workspaceId);
    await deleteDoc(workspaceRef);
  } catch (error) {
    console.error('Error deleting workspace:', error);
    throw new Error('Failed to delete workspace: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Get all documents for a workspace
 * @param {string} workspaceId - The workspace ID to get documents for
 * @returns {Promise<Array>} - Promise resolving to array of document objects
 */
export const getWorkspaceDocuments = async (workspaceId) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }

  try {
    // Use the subcollection path directly
    const q = query(
      collection(firestore, `workspaces/${workspaceId}/documents`),
      orderBy('updatedAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching workspace documents:', error);
    throw new Error('Failed to fetch workspace documents');
  }
};

/**
 * Get workspace by ID
 * @param {string} workspaceId - ID of the workspace to fetch
 * @returns {Promise<Object>} - Promise resolving to the workspace object
 */
export const getWorkspaceById = async (workspaceId) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }

  try {
    const workspaceRef = doc(firestore, 'workspaces', workspaceId);
    const workspaceSnap = await getDoc(workspaceRef);
    
    if (!workspaceSnap.exists()) {
      throw new Error('Workspace not found');
    }
    
    return {
      id: workspaceSnap.id,
      ...workspaceSnap.data()
    };
  } catch (error) {
    console.error('Error fetching workspace:', error);
    throw new Error('Failed to fetch workspace: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Get all documents in a workspace
 * @param {string} workspaceId - ID of the workspace
 * @returns {Promise<Object>} - Promise resolving to firestore snapshot of documents
 */
export const getDocumentsByWorkspace = async (workspaceId) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }
  
  try {
    const documentsQuery = query(
      collection(firestore, 'documents'),
      where('workspaceId', '==', workspaceId),
      orderBy('updatedAt', 'desc')
    );
    
    return await getDocs(documentsQuery);
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw new Error('Failed to fetch documents: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Delete a document
 * @param {string} documentId - ID of the document to delete
 * @returns {Promise<void>}
 */
export const deleteDocument = async (documentId) => {
  if (!documentId) {
    throw new Error('Document ID is required');
  }
  
  try {
    const documentRef = doc(firestore, 'documents', documentId);
    await deleteDoc(documentRef);
  } catch (error) {
    console.error('Error deleting document:', error);
    throw new Error('Failed to delete document: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Get all files in a workspace
 * @param {string} workspaceId - ID of the workspace
 * @returns {Promise<Object>} - Promise resolving to firestore snapshot of files
 */
export const getFilesByWorkspaceId = async (workspaceId) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }
  
  try {
    const filesQuery = query(
      collection(firestore, 'files'),
      where('workspaceId', '==', workspaceId),
      orderBy('updatedAt', 'desc')
    );
    
    return await getDocs(filesQuery);
  } catch (error) {
    console.error('Error fetching files:', error);
    throw new Error('Failed to fetch files: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Delete a file
 * @param {string} fileId - ID of the file to delete
 * @returns {Promise<void>}
 */
export const deleteFile = async (fileId) => {
  if (!fileId) {
    throw new Error('File ID is required');
  }
  
  try {
    // First get the file to check if it has a storage path
    const fileRef = doc(firestore, 'files', fileId);
    const fileSnap = await getDoc(fileRef);
    
    if (fileSnap.exists()) {
      const fileData = fileSnap.data();
      
      // If the file has a storage path, delete it from storage too
      if (fileData.storagePath) {
        const fileStorageRef = storageRef(storage, fileData.storagePath);
        try {
          await deleteObject(fileStorageRef);
        } catch (storageError) {
          console.error('Error deleting file from storage:', storageError);
          // Continue with document deletion even if storage deletion fails
        }
      }
    }
    
    // Then delete the file document
    await deleteDoc(fileRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw new Error('Failed to delete file: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Get all messages in a workspace
 * @param {string} workspaceId - ID of the workspace
 * @returns {Promise<Object>} - Promise resolving to firestore snapshot of messages
 */
export const getMessagesByWorkspaceId = async (workspaceId) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }
  
  try {
    const messagesQuery = query(
      collection(firestore, 'messages'),
      where('workspaceId', '==', workspaceId),
      orderBy('timestamp', 'asc')
    );
    
    return await getDocs(messagesQuery);
  } catch (error) {
    console.error('Error fetching messages:', error);
    throw new Error('Failed to fetch messages: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Delete a message
 * @param {string} messageId - ID of the message to delete
 * @returns {Promise<void>}
 */
export const deleteMessage = async (messageId) => {
  if (!messageId) {
    throw new Error('Message ID is required');
  }
  
  try {
    const messageRef = doc(firestore, 'messages', messageId);
    await deleteDoc(messageRef);
  } catch (error) {
    console.error('Error deleting message:', error);
    throw new Error('Failed to delete message: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Ensure workspace messages are persisted correctly
 * @param {string} workspaceId - The ID of the workspace
 * @param {object} message - The message object to save
 * @returns {Promise<string>} - Promise resolving to message ID
 */
export const saveWorkspaceMessage = async (workspaceId, message) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }
  
  if (!message) {
    throw new Error('Message is required');
  }
  
  try {
    // Add timestamp if not already present
    const messageWithTimestamp = {
      ...message,
      workspaceId,
      timestamp: message.timestamp || new Date().toISOString(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Save to main message collection
    const docRef = await addDoc(collection(firestore, 'workspace_messages'), messageWithTimestamp);
    
    // Also save to workspace-specific subcollection for better querying
    await setDoc(
      doc(firestore, `workspaces/${workspaceId}/messages`, docRef.id),
      messageWithTimestamp
    );
    
    console.log('Message saved to both collections:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('Error saving workspace message:', error);
    throw new Error('Failed to save message: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Get messages from a specific workspace with pagination
 * @param {string} workspaceId - ID of the workspace
 * @param {number} limit - Maximum number of messages to return
 * @param {object} lastMessage - Last message for pagination, or null for first page
 * @returns {Promise<Array>} - Promise resolving to array of messages
 */
export const getWorkspaceMessages = async (workspaceId, pageSize = 50, lastMessage = null) => {
  if (!workspaceId) {
    throw new Error('Workspace ID is required');
  }
  
  try {
    let messagesQuery;
    
    // Use the workspace-specific subcollection if it exists, otherwise fall back to main collection
    const workspaceMessagesRef = collection(firestore, `workspaces/${workspaceId}/messages`);
    const mainMessagesRef = collection(firestore, 'workspace_messages');
    
    // Check if workspace collection exists and has messages
    const testQuery = query(workspaceMessagesRef, firestoreLimit(1));
    const testSnapshot = await getDocs(testQuery);
    
    if (!testSnapshot.empty) {
      // Use workspace subcollection
      if (lastMessage) {
        messagesQuery = query(
          workspaceMessagesRef,
          orderBy('timestamp', 'asc'),
          startAfter(lastMessage),
          firestoreLimit(pageSize)
        );
      } else {
        messagesQuery = query(
          workspaceMessagesRef,
          orderBy('timestamp', 'asc'),
          firestoreLimit(pageSize)
        );
      }
    } else {
      // Fall back to main collection with filter
      if (lastMessage) {
        messagesQuery = query(
          mainMessagesRef,
          where('workspaceId', '==', workspaceId),
          orderBy('timestamp', 'asc'),
          startAfter(lastMessage),
          firestoreLimit(pageSize)
        );
      } else {
        messagesQuery = query(
          mainMessagesRef,
          where('workspaceId', '==', workspaceId),
          orderBy('timestamp', 'asc'),
          firestoreLimit(pageSize)
        );
      }
    }
    
    const snapshot = await getDocs(messagesQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error fetching workspace messages:', error);
    throw new Error('Failed to fetch messages: ' + (error.message || 'Unknown error'));
  }
};

/**
 * Add a user to a workspace
 * @param {string} workspaceId - ID of the workspace
 * @param {string} userId - ID of the user to add
 * @param {string} role - Role of the user (default: 'member')
 * @returns {Promise<Object>} - Promise resolving to the updated member object
 */
export const addUserToWorkspace = async (workspaceId, userId, userEmail, role = 'member') => {
  if (!workspaceId || !userId) {
    throw new Error('Workspace ID and user ID are required');
  }

  try {
    // Check if the workspace exists
    const workspaceRef = doc(firestore, 'workspaces', workspaceId);
    const workspaceSnap = await getDoc(workspaceRef);
    
    if (!workspaceSnap.exists()) {
      throw new Error('Workspace not found');
    }
    
    // Add user to workspace members array if not already there
    const workspaceData = workspaceSnap.data();
    if (!workspaceData.members || !workspaceData.members.includes(userId)) {
      await updateDoc(workspaceRef, {
        members: [...(workspaceData.members || []), userId],
        updatedAt: serverTimestamp()
      });
    }
    
    // Create or update the member document in the members subcollection
    const memberData = {
      userId,
      role,
      email: userEmail,
      joinedAt: serverTimestamp()
    };
    
    const memberRef = doc(firestore, 'workspaces', workspaceId, 'members', userId);
    await setDoc(memberRef, memberData);
    
    return {
      ...memberData,
      workspaceId
    };
  } catch (error) {
    console.error('Error adding user to workspace:', error);
    throw new Error('Failed to add user to workspace: ' + (error.message || 'Unknown error'));
  }
}; 
